import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import BudgetByDimensionReportTable from './BudgetByDimensionReportTable';
import { BudgetByDimensionReportTableProvider } from './context/BudgetByDimensionReportTableContext';

import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import TextFormField from 'CommonComponents/TextFormField';

import { ToastProvider } from 'Context/ToastContext';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

const ValidationSchema = Yup.object().shape({
    reviewer_name: Yup.string().max(255, $t('Your name must be at most 255 characters')).required($t('Your name is required')),
    comments: Yup.object()
        .test('comments-min', $t('Please add at least one comment before submitting'), (obj) => {
            if (!obj) return false;
            const nonEmpty = Object.values(obj).filter((v) => v && v.trim() !== '');
            return nonEmpty.length >= 1;
        })
        .test('comments-max-length', $t('Each comment must be at most 1000 characters'), (obj) => {
            if (!obj) return true;
            return Object.values(obj).every((v) => !v || v.length <= 1000);
        }),
});

/**
 * Interactive online-review page for Budgets by Venue / Budgets by Event reports.
 *
 * Mirrors the departments OnlineReview flow: it fetches the frozen snapshot data
 * (which carries its own dimensions / report_type / include_* criteria), renders
 * the flat report table with an editable per-line comment column, and submits the
 * reviewer's comments + name back to the signed update route.
 */
const BudgetByDimensionOnlineReview = ({ reviewDataUrl, reviewUpdateUrl, hasAnyReviewedActions, dimension }) => {
    const [success, setSuccess] = useState(false);

    const tost = useToast();

    const reviewDataRequest = useQuery({
        queryFn: () => apiClient.get(reviewDataUrl),
    });

    const report = reviewDataRequest.data?.report;

    if (success) {
        return (
            <div className="tw-p-8 tw-bg-green-50 tw-rounded-lg tw-text-green-900 tw-text-center">
                <h2 className="tw-text-2xl tw-font-bold tw-mb-4">{$t('Review submitted!')}</h2>
                <div>{$t('Thank you for your feedback. The sender will be notified.')}</div>
            </div>
        );
    }

    return (
        <Formik
            enableReinitialize
            initialValues={{
                comments: {},
                reviewer_name: reviewDataRequest.data?.reviewer_name || '',
            }}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
                apiClient
                    .put(reviewUpdateUrl, { comments: values.comments, reviewer_name: values.reviewer_name })
                    .then(() => {
                        setSuccess(true);
                        tost.success($t('Review submitted successfully.'));
                    })
                    .catch(() => {
                        tost.error($t('Failed to submit review.'));
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }}
        >
            {({ values, handleSubmit, isSubmitting, errors, isValid }) => (
                <div className="tw-flex tw-flex-col tw-space-y-6">
                    <BudgetByDimensionReportTableProvider
                        criteria={report?.criteria}
                        report={report}
                        dimensions={report?.dimensions}
                        dimension={report?.dimension || dimension}
                        comments={values.comments}
                        allComments={reviewDataRequest.data?.comments || {}}
                        userType={reviewDataRequest.data?.user_type || 'reviewer'}
                        loading={reviewDataRequest.loading}
                        showComments
                        editableComments
                    >
                        <BudgetByDimensionReportTable />
                    </BudgetByDimensionReportTableProvider>
                    {errors.comments && <div className="tw-text-red-600 tw-text-sm">{errors.comments}</div>}
                    <div className="tw-flex tw-items-start tw-justify-end tw-gap-4 tw-pb-8">
                        <TextFormField
                            name="reviewer_name"
                            placeholder={$t('Enter your name')}
                            required={true}
                            disabled={isSubmitting || reviewDataRequest.loading || hasAnyReviewedActions}
                            hideLabel
                        />
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={!isValid || isSubmitting || reviewDataRequest.loading}
                        >
                            {$t('Submit Review')}
                        </Button>
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default BudgetByDimensionOnlineReview;

if (document.getElementById('BudgetByDimensionOnlineReviewApp')) {
    const rootEl = document.getElementById('BudgetByDimensionOnlineReviewApp');

    // Data attributes
    const reviewDataUrl = rootEl.getAttribute('data-review-data-url');
    const reviewUpdateUrl = rootEl.getAttribute('data-review-update-url');
    const hasAnyReviewedActions = rootEl.getAttribute('data-has-any-reviewed-actions') === 'true';
    const dimension = rootEl.getAttribute('data-dimension') || 'venue';

    ReactDOM.render(
        <ToastProvider>
            <BudgetByDimensionOnlineReview
                reviewDataUrl={reviewDataUrl}
                reviewUpdateUrl={reviewUpdateUrl}
                hasAnyReviewedActions={hasAnyReviewedActions}
                dimension={dimension}
            />
        </ToastProvider>,
        rootEl,
    );
}
